import { computed } from 'vue'

import type { Component, ComputedRef } from 'vue'
import type { ScopeGateArea, ScopeSkeleton } from '@shared/lib/scope'
import type { ScopeStatus } from '@shared/lib/scope'

import { useScopeReadiness } from '@shared/lib/scope'
import { LoaderBlockDomain } from '@shared/ui/loaders'

import SkeletonSectionDemo from './SkeletonSectionDemo.vue'
import SkeletonAllLessons from './SkeletonAllLessons.vue'
import SkeletonProgressReports from './SkeletonProgressReports.vue'
import SkeletonLearningProgress from './SkeletonLearningProgress.vue'
import SkeletonChangeWeeklySchedule from './SkeletonChangeWeeklySchedule.vue'
import SkeletonFilesSection from './SkeletonFilesSection.vue'

interface GateScopeOptions {
  area: ScopeGateArea
}

interface GateScopeReturn {
  status: ComputedRef<ScopeStatus>
  isSkeletonShown: ComputedRef<boolean>
  skeletonComponent: ComputedRef<Component>
  retry: () => void
}

const SKELETON_COMPONENTS: Record<ScopeSkeleton, Component> = {
  demo: SkeletonSectionDemo,
  lessons: SkeletonAllLessons,
  progressReports: SkeletonProgressReports,
  learningProgress: SkeletonLearningProgress,
  schedule: SkeletonChangeWeeklySchedule,
  files: SkeletonFilesSection,
  block: LoaderBlockDomain,
}

const isSkeletonStatus = (status: ScopeStatus): boolean => {
  switch (status) {
    case 'loading':
      return true
    case 'error':
      // TODO(INFRA-724): error block with retry
      return false
    case 'idle':
    case 'ready':
      return false
  }
}

export const useGateScope = ({ area }: GateScopeOptions): GateScopeReturn => {
  const { status, skeleton, gate, retry } = useScopeReadiness()

  const isGatedHere = computed<boolean>(() => gate.value === area)

  const isSkeletonShown = computed<boolean>(() => isGatedHere.value && isSkeletonStatus(status.value))

  const skeletonComponent = computed<Component>(() => SKELETON_COMPONENTS[skeleton.value])

  return {
    status,
    isSkeletonShown,
    skeletonComponent,
    retry,
  }
}
